import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import walletPageMessages from '../../../messages/wallet/wallet-page.messages';
import {
  Product,
  ProductMacroCategory,
  TabItem,
  Wallet,
  WalletSummary,
} from '../../../model';
import { GlobalActions } from '../../../model';
import {
  dossierSliceActions,
  messagesForDossierError,
  MyWalletSections,
  selectDossierState,
} from '../../../store';
import {
  getCurrentState,
  getSelectedWalletSection,
  messagesForWalletError,
  myWalletSliceActions,
  retrieveProductsPerformances,
  retrieveWallet,
  sharedAccountsSliceActions,
  updateShowBalance,
} from '../../../store';
import { useAppDispatch, useAppSelector } from '../../redux';
import AppRoutes from '../../../routing/app-routes';
import { useAppNavigation } from '../../../utils/navigation.utils';

export interface MyWalletLogic {
  wallet: Wallet;
  sortedMacroCategories: ProductMacroCategory[];
  isWalletEmpty: boolean;
  isProductEmpty: boolean;
  sections: TabItem<MyWalletSections>[];
  selectedSection: MyWalletSections;
  isLoadingWallet: boolean;
  isLoadingSummary: boolean;
  isLoadingProductPerformances: boolean;
  flagOpenProductDetails: boolean;
  flagShowBalance: boolean;
  summary: WalletSummary;

  onSelectedSection: (selectedSection: MyWalletSections) => void;
  selectedProduct: (product: Product) => void;
  setFlagShowBalance: (value: boolean) => void;
  closeProductDetails: () => void;
  goToRealEconomyDetailPage: () => void;
}

export const useMyWalletLogic = (
  globalActions: GlobalActions
): MyWalletLogic => {
  const { formatMessage } = useIntl();
  const { appNavigate } = useAppNavigation();

  const initialSections: TabItem<MyWalletSections>[] = [
    {
      type: 'POSITION_LIST',
      label: formatMessage(walletPageMessages.positionToggleLabel),
    },
    {
      type: 'PERFORMANCE',
      label: formatMessage(walletPageMessages.performanceToggleLabel),
    },
  ];
  const [sections, setSections] =
    useState<TabItem<MyWalletSections>[]>(initialSections);
  const { showToast, closeToast } = globalActions;
  const dispatch = useAppDispatch();

  const [retrieveWalletCounter, setRetrieveWalletCounter] = useState(0);

  const maxRetry = 1;

  const {
    flagOpenProductDetails,
    wallet,
    summary: walletSummary,
    walletRequestStatus,
    walletError,
    productsPerformancesRequestStatus,
    productsPerformancesError,
    myWalletSection,
  }: any = useAppSelector(getCurrentState);
  const flagShowBalance: boolean = useAppSelector(
    (state) => state.data?.user?.showBalance ?? false
  );

  const summary: WalletSummary = useMemo(() => {
    if (!wallet?.macroCategories) return null as any;

    const macroCategories = wallet.macroCategories;
    const liquidityFromSummary = walletSummary?.macroCategories?.find(
      (category) => category.type === 'Liquidity'
    )?.amount;
    const totalPortfolio = macroCategories.find(
      (c) => c.type === 'TotalPortfolio'
    );
    const totalFia = macroCategories.find((c) => c.type === 'TotalFia');

    const totalAmountValue =
      (totalPortfolio?.amount?.value || 0) - (totalFia?.amount?.value || 0);
    const currency = totalPortfolio?.amount?.currency || 'EUR';

    const managed = macroCategories.find((c) => c.type === 'Managed');
    const financialInsurance = macroCategories.find(
      (c) => c.type === 'FinancialInsurance'
    );

    const managedFinancialInsuranceAmount =
      (managed?.amount?.value || 0) + (financialInsurance?.amount?.value || 0);

    const otherCategories = macroCategories.filter(
      (c) =>
        ![
          'Managed',
          'FinancialInsurance',
          'TotalPortfolio',
          'TotalFia',
        ].includes(c.type)
    );

    const summaryMacroCategories: any[] = [
      {
        type: 'ManagedFinancialInsurance',
        amount: { value: managedFinancialInsuranceAmount, currency },
        gain: 0,
      },
      ...otherCategories.map((c) => ({
        type: c.type,
        amount:
          c.type === 'Liquidity'
            ? liquidityFromSummary || c.amount || { value: 0, currency }
            : c.amount || { value: 0, currency },
        gain: 0,
      })),
    ];

    return {
      totalAmount: { value: totalAmountValue, currency },
      macroCategories: summaryMacroCategories,
    };
  }, [wallet?.macroCategories]);

  const { dossierFilter, dossierFilterError, dossierFilterRequestStatus } =
    useAppSelector(selectDossierState);

  const selectedWalletSection = useAppSelector(getSelectedWalletSection);

  const actions = useMemo(
    () =>
      selectedWalletSection === 'MY_WALLET'
        ? myWalletSliceActions
        : sharedAccountsSliceActions,
    [selectedWalletSection]
  );

  const setFlagShowBalance = useCallback(
    (showBalance: boolean) => {
      dispatch(updateShowBalance(showBalance));
    },
    [dispatch]
  );

  const selectedProduct = useCallback(
    (product: Product) => {
      dispatch(actions.setSelectedProduct(product));
    },
    [actions, dispatch]
  );

  const closeProductDetails = useCallback(() => {
    dispatch(actions.clearSelectedProduct());
    dispatch(actions.clearProductDetails());
  }, [dispatch, actions]);

  useEffect(() => {
    if (dossierFilter) {
      if (
        !wallet &&
        walletRequestStatus !== 'loading' &&
        retrieveWalletCounter < maxRetry
      ) {
        dispatch(retrieveWallet());
        setRetrieveWalletCounter(retrieveWalletCounter + 1);
      }
      if (dossierFilterRequestStatus === 'succeeded') {
        dispatch(dossierSliceActions.clearDossierError());
      }
    }
  }, [
    dispatch,
    dossierFilterRequestStatus,
    actions,
    summary,
    wallet,
    walletRequestStatus,
    dossierFilter,
    retrieveWalletCounter,
  ]);

  useEffect(() => {
    if (walletError) {
      const errorMessages = messagesForWalletError(walletError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(actions.clearWalletError());
    }
  }, [dispatch, walletError, closeToast, formatMessage, showToast, actions]);

  useEffect(() => {
    if (dossierFilterError) {
      const errorMessages = messagesForDossierError(dossierFilterError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(dossierSliceActions.clearDossierError());
    }
  }, [dispatch, dossierFilterError, closeToast, formatMessage, showToast]);

  useEffect(() => {
    if (productsPerformancesError) {
      const errorMessages = messagesForWalletError(productsPerformancesError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(actions.clearProductsPerformancesError());
    }
  }, [
    dispatch,
    productsPerformancesError,
    closeToast,
    formatMessage,
    showToast,
    actions,
  ]);

  const onSelectedSection = useCallback(
    (section: MyWalletSections) => {
      setSections(
        sections.map((item) => ({
          ...item,
          isSelected: item.type === section,
        }))
      );
      dispatch(actions.setMyWalletSection(section));
      if (section === 'PERFORMANCE' && !wallet?.performances && dossierFilter) {
        dispatch(retrieveProductsPerformances());
      }
    },
    [sections, wallet?.performances, dossierFilter, dispatch]
  );

  const goToRealEconomyDetailPage = useCallback(() => {
    appNavigate(AppRoutes.WalletErDetail);
  }, [appNavigate]);

  const isWalletEmpty = useMemo(
    () =>
      summary === null ||
      wallet?.macroCategories?.every(
        (macroCategory) => (macroCategory.amount?.value || 0) === 0
      ),
    [summary, wallet?.macroCategories]
  );

  const isProductEmpty = useMemo(
    () => (wallet ? wallet.performances?.products?.length === 0 : true),
    [wallet]
  );

  const MACRO_CATEGORY_ORDER = [
    'Managed',
    'RealEconomy',
    'FinancialInsurance',
    'Welfare',
    'Administered',
    'Liquidity',
    'TotalFia',
    'TotalPortfolio',
  ];

  const sortedMacroCategories: ProductMacroCategory[] = useMemo(() => {
    if (!wallet?.macroCategories) return [];

    return [...wallet.macroCategories].sort((a, b) => {
      const indexA = MACRO_CATEGORY_ORDER.indexOf(a.type);
      const indexB = MACRO_CATEGORY_ORDER.indexOf(b.type);

      // Se una categoria non è nell'ordine definito, la mette alla fine
      const orderA = indexA === -1 ? MACRO_CATEGORY_ORDER.length : indexA;
      const orderB = indexB === -1 ? MACRO_CATEGORY_ORDER.length : indexB;

      return orderA - orderB;
    });
  }, [wallet?.macroCategories]);

  return {
    wallet,
    sortedMacroCategories,
    isWalletEmpty,
    isProductEmpty,
    sections,
    selectedSection: myWalletSection,
    isLoadingWallet:
      walletRequestStatus === 'loading' ||
      dossierFilterRequestStatus === 'loading',
    isLoadingSummary:
      walletRequestStatus === 'loading' ||
      dossierFilterRequestStatus === 'loading',
    isLoadingProductPerformances:
      productsPerformancesRequestStatus === 'loading',
    flagOpenProductDetails,
    flagShowBalance,
    summary,
    onSelectedSection,
    setFlagShowBalance,
    selectedProduct,
    closeProductDetails,
    goToRealEconomyDetailPage,
  };
};
